import { formatDateRu } from '@pms/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { request } from '../../sync/api.js';

/**
 * Несданные смены.
 *
 * Считается по агрегатам: отчёт с первого пресса не закрывает смену
 * на остальных трёх. Показываем, кто был назначен, — чтобы было с кого спросить.
 */

interface Missing {
  shiftId: string;
  siteId: string;
  siteName: string;
  date: string;
  shiftNumber: number;
  machineId: string;
  machineName: string;
  endAt: string;
  assignedUsers: Array<{ id: string; fullName: string }>;
}

export function MissingShiftsScreen(): JSX.Element {
  const [missing, setMissing] = useState<Missing[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const response = await request<{ missing: Missing[] }>(
          '/api/reports/missing',
          { method: 'GET' },
          20_000,
        );
        setMissing(response.missing);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить список');
      }
    })();
  }, []);

  if (!missing) {
    return (
      <div className="min-h-screen px-4 py-6">
        <p className="text-lg text-dim">{error ?? 'Загружаю…'}</p>
        <Link to="/admin" className="mt-4 inline-block rounded-xl bg-muted px-4 py-3 text-base">
          Назад
        </Link>
      </div>
    );
  }

  const byDate = new Map<string, Missing[]>();
  for (const item of missing) {
    byDate.set(item.date, [...(byDate.get(item.date) ?? []), item]);
  }

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Несданные смены</h1>
        <Link to="/admin" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      {missing.length === 0 ? (
        <p className="rounded-xl bg-emerald-800 p-4 text-lg font-semibold text-white">
          Всё сдано
        </p>
      ) : (
        <>
          <p className="mb-4 text-base text-dim">
            Смены, которые закончились больше часа назад, а отчёта по агрегату нет.
            Сдать за сотрудника может администратор — с указанием причины.
          </p>

          {[...byDate.entries()].map(([date, items]) => (
            <section key={date} className="mb-6">
              <h2 className="mb-2 text-lg font-semibold">{formatDateRu(date)}</h2>
              <ul className="flex flex-col gap-touch">
                {items.map((item) => (
                  <li
                    key={`${item.shiftId}-${item.machineId}`}
                    className="rounded-xl bg-rose-50 text-rose-800 ring-1 ring-rose-200 p-4"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-lg font-semibold">
                        {item.siteName} · {item.machineName}
                      </span>
                      <span className="text-base text-body">смена {item.shiftNumber}</span>
                    </div>
                    <p className="mt-2 text-base text-body">
                      {item.assignedUsers.length > 0
                        ? item.assignedUsers.map((person) => person.fullName).join(', ')
                        : 'сотрудник не назначен'}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
